import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAuth } from './AuthContext';

interface Organization {
  id: string;
  name: string;
  slug: string;
  currency?: string;
  user_role?: string;
}

interface OrganizationContextType {
  organizations: Organization[];
  selectedOrg: Organization | null;
  userRole: string | null;
  setSelectedOrg: (org: Organization | null) => void;
  refreshOrganizations: (selectNewest?: boolean) => Promise<void>;
  canEdit: boolean;
  canDelete: boolean;
  canCreate: boolean;
  canManageTeam: boolean;
  canChat: boolean;
  isViewer: boolean;
  isEditor: boolean;
  isAdmin: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined,
);

import { API_URL } from '../constants/urls';

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrgState] = useState<Organization | null>(
    null,
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Derived state for user role and permissions
  const userRole = selectedOrg?.user_role || null;
  const isViewer = userRole === 'viewer';
  const isEditor = userRole === 'editor';
  const isAdmin = userRole === 'admin';

  const canEdit = isAdmin || isEditor;
  const canDelete = isAdmin || isEditor;
  const canCreate = isAdmin || isEditor;
  const canManageTeam = isAdmin;
  const canChat = isAdmin || isEditor;

  const refreshOrganizations = async (selectNewest = false) => {
    if (!token || isRefreshing) return;

    try {
      setIsRefreshing(true);
      const response = await fetch(`${API_URL}/auth/me/organizations`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        const previousOrgIds = organizations.map((o) => o.id);
        setOrganizations(data);

        if (selectNewest && data.length > organizations.length) {
          // Find the newly created organization (one that wasn't in the previous list)
          const newOrg = data.find(
            (o: Organization) => !previousOrgIds.includes(o.id),
          );
          if (newOrg) {
            setSelectedOrgState(newOrg);
            // Also update localStorage so it persists on refresh
            localStorage.setItem('selectedOrgId', newOrg.id);
            localStorage.setItem(
              'selectedOrganization',
              JSON.stringify(newOrg),
            );
          } else {
            // Fallback to last organization (newest in ascending order)
            const lastOrg = data[data.length - 1];
            setSelectedOrgState(lastOrg);
            localStorage.setItem('selectedOrgId', lastOrg.id);
            localStorage.setItem(
              'selectedOrganization',
              JSON.stringify(lastOrg),
            );
          }
        } else {
          // Restore selected org from localStorage
          const savedOrgId = localStorage.getItem('selectedOrgId');
          const found = data.find((o: Organization) => o.id === savedOrgId);

          if (found) {
            setSelectedOrgState(found);
          } else if (data.length > 0) {
            setSelectedOrgState(data[0]);
          }
        }
      }
    } catch (error) {
      // Error handling for organization fetch
    } finally {
      setIsRefreshing(false);
    }
  };

  const setSelectedOrg = (org: Organization | null) => {
    setSelectedOrgState(org);
    if (org) {
      localStorage.setItem('selectedOrgId', org.id);
      // Store the full org with role for other components
      localStorage.setItem('selectedOrganization', JSON.stringify(org));
    } else {
      localStorage.removeItem('selectedOrgId');
      localStorage.removeItem('selectedOrganization');
    }
  };

  useEffect(() => {
    refreshOrganizations();
  }, [token]);

  const value: OrganizationContextType = {
    organizations,
    selectedOrg,
    userRole,
    setSelectedOrg,
    refreshOrganizations,
    canEdit,
    canDelete,
    canCreate,
    canManageTeam,
    canChat,
    isViewer,
    isEditor,
    isAdmin,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error(
      'useOrganization must be used within an OrganizationProvider',
    );
  }
  return context;
}
